import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import QEStrategyCommonButton from "../qe-strategy-homepage/CommonButton";
import { QEStrategydataFlowContext } from "../../../main-pages/QEStrategyMainPage";
import { usePrefillForm } from "../../../../utils/qe-strategy-utils/usePreFillForm";
import {
  getUserByEmail,
  storeCsvDataInDB,
} from "../../../../services/webServices";
import { getStructuredDataOnly } from "../../../../utils/qe-strategy-utils/dataFormatter";
import { generateFlatCSVFromContext } from "../../../../utils/qe-strategy-utils/flattenCSV";
import { useNavigate } from "react-router-dom";
import LinearProgressBar from "../../../common-components/QMentisLinearProgressBar";
import MessagePopup from "../../../popup-components/MessagePopup";
import SessionExpired from "../../../popup-components/SessionExpiredPopup";

const DataCentricAndCapabilityAlignedQA = ({ onBack }) => {
  // --- State Variables ---
  const [dataLineage, setDataLineage] = useState("");
  const [mocksUsed, setMocksUsed] = useState("");
  const [nonQeRoles, setNonQeRoles] = useState("");
  const [alignedWithBusiness, setAlignedWithBusiness] = useState("");
  const [adaptedOnChanges, setAdaptedOnChanges] = useState("");
  const [legacyVsModern, setLegacyVsModern] = useState("");
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const {
    businessContextAndRiskLandscape,
    applicationArchitectureAndDataIntegration,
    devopsTestAutomationAndDeveloperExperience,
    aiMLLLMConsideration,
    observabilityResilienceSecurityAndScalability,
    qualityCultureGovernanceAndTestDebt,
    dataCentricAndCapabilityAlignedQE,
    setDataCentricAndCapabilityAlignedQE,
    dataFromFileUploads,
    structuredCSVContext,
    setStructuredCSVContext,
  } = useContext(QEStrategydataFlowContext);
  const navigate = useNavigate();
  // --- Handlers ---
  const handleDataLineageChange = (e) => setDataLineage(e.target.value);
  const handleMocksUsedChange = (e) => setMocksUsed(e.target.value);
  const handleNonQeRolesChange = (e) => setNonQeRoles(e.target.value);
  const handleAlignedWithBusinessChange = (e) =>
    setAlignedWithBusiness(e.target.value);
  const handleAdaptedOnChangesChange = (e) =>
    setAdaptedOnChanges(e.target.value);
  const handleLegacyVsModernChange = (e) => setLegacyVsModern(e.target.value);

  useEffect(() => {
    if (dataCentricAndCapabilityAlignedQE) {
      setDataLineage(dataCentricAndCapabilityAlignedQE.dataLineage || "");
      setMocksUsed(dataCentricAndCapabilityAlignedQE.mocksUsed || "");
      setNonQeRoles(dataCentricAndCapabilityAlignedQE.nonQeRoles || "");
      setAlignedWithBusiness(
        dataCentricAndCapabilityAlignedQE.alignedWithBusiness || ""
      );
      setAdaptedOnChanges(
        dataCentricAndCapabilityAlignedQE.adaptedOnChanges || ""
      );
      setLegacyVsModern(dataCentricAndCapabilityAlignedQE.legacyVsModern || "");
    }
  }, [dataCentricAndCapabilityAlignedQE]);

  const dataCentricAndCapabilityAlignedQEDataObject = {
    dataLineage,
    mocksUsed,
    nonQeRoles,
    alignedWithBusiness,
    adaptedOnChanges,
    legacyVsModern,
  };

  const handleBack = () => {
    setDataCentricAndCapabilityAlignedQE(
      dataCentricAndCapabilityAlignedQEDataObject
    );
    onBack();
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setLoader(true);
    // Update state with latest data of Data Centric and Capability Aligned
    setDataCentricAndCapabilityAlignedQE(
      dataCentricAndCapabilityAlignedQEDataObject
    );

    // Get structured data from context
    const structuredData = getStructuredDataOnly({
      businessContextAndRiskLandscape,
      applicationArchitectureAndDataIntegration,
      devopsTestAutomationAndDeveloperExperience,
      aiMLLLMConsideration,
      observabilityResilienceSecurityAndScalability,
      qualityCultureGovernanceAndTestDebt,
      dataCentricAndCapabilityAlignedQE:
        dataCentricAndCapabilityAlignedQEDataObject,
    });

    // Generate structured CSV (raw)
    const structuredCSV = generateFlatCSVFromContext(
      structuredData,
      "structured"
    );

    // Helper to create Blob from CSV string
    const createCSVBlob = (csvString) =>
      new Blob([csvString], { type: "text/csv;charset=utf-8;" });

    // Create blob for raw structured data
    // const structuredBlob = createCSVBlob(structuredCSV);

    // // Helper to download a CSV
    // const downloadCSV = (blob, filename) => {
    //   const url = URL.createObjectURL(blob);
    //   const link = document.createElement("a");
    //   link.href = url;
    //   link.setAttribute("download", filename);
    //   document.body.appendChild(link);
    //   link.click();
    //   document.body.removeChild(link);
    //   URL.revokeObjectURL(url);
    // };

    // // Download raw structured CSV locally
    // console.log("Downloading original structured CSV...");
    // downloadCSV(structuredBlob, "QE_Structured_Data.csv");

    const combinedCSV = structuredCSV;

    // Create final Blob for structured CSV
    const finalBlob = createCSVBlob(combinedCSV);

    // Upload final combined CSV to DB
    try {
      const usernameResponse = await getUserByEmail();
      const userData = usernameResponse?.data?.data[0]; // Get first user from array
      const username = userData.username;

      // Get current date and time in IST (Indian Standard Time)
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000; // IST = UTC + 5:30
      const istTime = new Date(now.getTime() + istOffset - now.getTimezoneOffset() * 60 * 1000
      );

      // Format as yyyy-mm-dd_hh-mm-ss
      const formattedTimestamp = istTime.toISOString().replace(/[-:]/g, "").replace("T", "_").split(".")[0];

      // Create filename with username and IST timestamp
      const filename = `${username}_${formattedTimestamp}.csv`;

      // Store the CSV data in DB with the new filename
      const response = await storeCsvDataInDB(finalBlob, filename);

      navigate("/dashboard/qe-strategy/archetype-predicted", {
        state: {
          heatmap: response.charts.heatmap,
          primary_radar_chart: response.charts.primary_radar,
          secondary_radar_chart: response.charts.secondary_radar,
          tertiary_radar_chart: response.charts.tertiary_radar,
          pie_chart: response.charts.pie_chart,
          predicted_archetypes: response.top_classes,
          all_architectures: response.all_architectures,
          uploadedCSV: structuredCSV,
        },
      });
    } catch (error) {
      console.error("Unexpected error:", error);

      if (error.response?.data?.message === "Your session has expired.") {
        setSessionExpired(true);
        setIsErrorPopupOpen(false);
      } else {
        // Show popup for all other errors
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          "An unexpected error occurred. Please try again.";
        setErrorMessage(msg);
        setIsErrorPopupOpen(true);
      }
    } finally {
      setLoader(false);
    }
  };

  const questionToFieldMap = {
    "1. Is data lineage tracked during test validation?": "dataLineage",
    "2. Are mocks, toggles, or test hooks commonly used?": "mocksUsed",
    "3. Do non-QE roles (e.g., SRE, product) contribute to test design?":
      "nonQeRoles",
    "4. Are QE strategies aligned with business capability criticality?":
      "alignedWithBusiness",
    "5. Is QE strategy adapted when system architecture changes?":
      "adaptedOnChanges",
    "6. Are legacy and modern systems tested differently?": "legacyVsModern",
  };

  usePrefillForm(dataFromFileUploads, questionToFieldMap, {
    dataLineage: setDataLineage,
    mocksUsed: setMocksUsed,
    nonQeRoles: setNonQeRoles,
    alignedWithBusiness: setAlignedWithBusiness,
    adaptedOnChanges: setAdaptedOnChanges,
    legacyVsModern: setLegacyVsModern,
  });

  return (
    <Box
      sx={{
        width: {
          xs: "100%", // full width on small screens
          sm: "100%",
          md: "1030px", // fixed width on medium and above
        },

        mx: "auto", // center horizontally

        mt: { xs: 5, sm: 8, md: 3 },
        mb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 }, // side padding only for smaller screens
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: 20, position: "relative", top: "-14px" }}
        >
          Data-Centric & Capability-Aligned QE
        </Typography>

        <Grid container spacing={3}>
          {/* Data Lineage */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Is data lineage tracked during test validation?
              </FormLabel>
              <RadioGroup
                row
                value={dataLineage}
                onChange={handleDataLineageChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Mocks Used */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are mocks, toggles, or test hooks commonly used?
              </FormLabel>
              <RadioGroup
                row
                value={mocksUsed}
                onChange={handleMocksUsedChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="sometimes"
                  control={<Radio />}
                  label="Sometimes"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Non-QE Roles */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Do non-QE roles (e.g., SRE, product) contribute to test design?
              </FormLabel>
              <RadioGroup
                row
                value={nonQeRoles}
                onChange={handleNonQeRolesChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="sometimes"
                  control={<Radio />}
                  label="Sometimes"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Business Criticality */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are QE strategies aligned with business capability criticality?
              </FormLabel>
              <RadioGroup
                row
                value={alignedWithBusiness}
                onChange={handleAlignedWithBusinessChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Adapted on Architecture Changes */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Is QE strategy adapted when system architecture changes?
              </FormLabel>
              <RadioGroup
                row
                value={adaptedOnChanges}
                onChange={handleAdaptedOnChangesChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Legacy vs Modern */}
          <Grid item xs={12}>
            <FormControl>
              <FormLabel>
                Are legacy and modern systems tested differently?
              </FormLabel>
              <RadioGroup
                row
                value={legacyVsModern}
                onChange={handleLegacyVsModernChange}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel
                  value="Partially"
                  control={<Radio />}
                  label="Partially"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Buttons outside Paper */}
      <QEStrategyCommonButton
        handleBack={handleBack}
        nextText="Submit"
        handleNext={handleGenerateReport}
      />
      {isErrorPopupOpen && (
        <MessagePopup
          modalOpen={isErrorPopupOpen}
          okayHandler={() => setIsErrorPopupOpen(false)}
          message={errorMessage}
        />
      )}

      {sessionExpired && <SessionExpired />}

      {loader && <LinearProgressBar label={"Analyzing..."} />}
    </Box>
  );
};

export default DataCentricAndCapabilityAlignedQA;
